/* eslint-disable no-underscore-dangle */
import { useMemo } from 'react';
import { css, useMultiStyleConfig, useTheme } from '@chakra-ui/react';

import { CssType } from '../@types';
import { moduleToCss } from '../utils/css';
import { createLogLocal } from '../utils/createLogLocal';
import { getNestedValue } from '../utils/getNestedValue';

const log = createLogLocal('useHostTheme.tsx');

export const transformCss = (el: any, colors: CssType, sizes: CssType) => {
  const colorValue = getNestedValue(colors, el, null);
  const sizeValue = getNestedValue(sizes, el, null);
  return colorValue || sizeValue || el;
};

const generateBase = (
  field: Record<string, any>,
  variables: { [x: string]: any },
) => {
  // Filter out properties that include '&' or start with '--'
  const chakraBase = Object.fromEntries(
    Object.entries(field).filter(
      ([prop]) => !prop.includes('&') && !prop.startsWith('--'),
    ),
  );

  return (
    Object.fromEntries(
      Object.keys(chakraBase).map(el => [
        el,
        variables[chakraBase[el]] || chakraBase[el],
      ]),
    ) || {}
  );
};

const generatePseudo = (
  pseudo: string,
  field: Record<string, any>,
  variables: { [x: string]: any },
) => {
  // Select properties that include '&' and the pseudo selector
  const picked = Object.fromEntries(
    Object.entries(field).filter(
      ([prop]) => prop.includes('&') && prop.includes(pseudo),
    ),
  );
  const chakraPseudo = picked[Object.keys(picked)[0]] || {};
  return (
    Object.fromEntries(
      Object.keys(chakraPseudo)
        .filter(el => Boolean(variables[chakraPseudo[el]]))
        .map(el => [el, variables[chakraPseudo[el]]]),
    ) || {}
  );
};

export const flattenStyles = (
  styles: Record<string, any>,
  overrides?: Record<string, string>,
) =>
  Object.keys(styles)
    .map(
      el =>
        `${moduleToCss(el)}: ${overrides?.[el] ? overrides[el] : styles[el]}`,
    )
    .join('; ');

export const parseStyles = (
  // styles: Record<string, any>,
  styles: string,
) =>
  Object.fromEntries(
    styles.split(';').map(el => el.split(':').map(s => s.trim())),
  );

export const useHostThemeForTokenex = () => {
  const chakraTheme = useTheme();

  // window.console.log('Host Chakra Theme', chakraTheme);
  const chakraThemeCssVars = Object.fromEntries(
    Object.keys(chakraTheme.__cssVars || {})
      .filter(key => key.startsWith('--'))
      .map(key => [`var(${key})`, chakraTheme.__cssVars[key]]),
  );

  const inputStyles = useMultiStyleConfig('Input', {});

  const tokenexTheme = useMemo(
    () => css(inputStyles)(chakraTheme),
    [inputStyles, chakraTheme],
  );

  // Select properties that start with '--'
  const inputVariablesDefs: Record<string, string> = Object.fromEntries(
    Object.entries(tokenexTheme.field || {}).filter(([prop]) =>
      prop.startsWith('--'),
    ),
  ) as Record<string, string>;

  const chakraVariables = {
    ...chakraThemeCssVars,
    ...Object.fromEntries(
      Object.keys(inputVariablesDefs || {}).map(el => {
        const value = inputVariablesDefs[el];
        return [
          `var(${el})`,
          typeof value === 'string' ? chakraThemeCssVars[value] : undefined,
        ];
      }),
    ),
  };

  const tokenexPseudoDisabled = generatePseudo(
    'disabled',
    tokenexTheme.field,
    chakraVariables,
  );
  const tokenexPseudoInvalid = generatePseudo(
    'invalid',
    tokenexTheme.field,
    chakraVariables,
  );
  const tokenexPseudoPlaceholder = generatePseudo(
    'placeholder',
    tokenexTheme.field,
    chakraVariables,
  );
  const tokenexPseudoHover = generatePseudo(
    'hover',
    tokenexTheme.field,
    chakraVariables,
  );
  const tokenexPseudoFocus = generatePseudo(
    'focus',
    tokenexTheme.field,
    chakraVariables,
  );

  const tokenexBasicStyles = generateBase(tokenexTheme.field, chakraVariables);

  if (!tokenexBasicStyles.height || tokenexBasicStyles.height === 'auto') {
    tokenexBasicStyles.height = '24px';
    log(
      '[INFO] Input control height can not be "auto", changed to "24px" instead.',
    );
  }

  if (!tokenexBasicStyles.padding || tokenexBasicStyles.padding === 'auto') {
    tokenexBasicStyles.padding = '0px';
    log(
      '[INFO] Input control padding can not be "auto", changed to "0px" instead.',
    );
  }
  tokenexBasicStyles.boxSizing = 'border-box';
  log('[INFO] Input control box-sizing set to "border-box".');

  if (
    !tokenexBasicStyles.fontFamily ||
    tokenexBasicStyles.fontFamily.includes('var(')
  ) {
    tokenexBasicStyles.fontFamily = '"Poppins"';
  }
  tokenexBasicStyles.fontFamily += ', Arial, Helvetica, sans-serif';

  // TODO: we may need to use default 'reset' style for the control for all consumers
  // and then let them override only wrapping container styles.
  // right now, this, dynamic aproach, only produces problems
  const cssOverides = {
    border: '0px !important',
    borderWidth: '0px !important',
    borderBottom: '0px  !important',
    borderTop: '0px !important',
    borderLeft: '0px !important',
    borderRight: '0px !important',
  };

  return {
    css: {
      base: flattenStyles(tokenexBasicStyles, cssOverides),
      focus: flattenStyles(tokenexPseudoFocus, cssOverides),
      hover: flattenStyles(tokenexPseudoHover, cssOverides),
      error: flattenStyles(tokenexPseudoInvalid, cssOverides),
      placeholder: flattenStyles(tokenexPseudoPlaceholder, cssOverides),
      disabled: flattenStyles(tokenexPseudoDisabled, cssOverides),
    },
    module: {
      base: tokenexBasicStyles,
      focus: tokenexPseudoFocus,
      hover: tokenexPseudoHover,
      error: tokenexPseudoInvalid,
      placeholder: tokenexPseudoPlaceholder,
      disabled: tokenexPseudoDisabled,
    },
  };
};
